import { CloseCircleOutlined } from '@ant-design/icons';
import { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { CenterFlex, Flex } from '@/components/base';
import Tooltip from '@/components/Tooltip/TooltipV2';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';

import FilterItemOperatorSelector from './FilterItemOperatorSelector';
import FilterItemSelect from './FilterItemSelect';

const FilterItem = (props: FilterItemProps) => {
  const color = useColor();
  const [filterDropdownState, setFilterDropdownState] =
    useState<FilterOptionT | null>(null);

  const filterRef = useRef<HTMLDivElement>(null);

  const dropdownOpenStatus = useMemo(
    () => filterDropdownState !== null,
    [filterDropdownState],
  );

  const filterStatusList = useMemo(() => {
    return props.currFilterQueryFormat.filterValues;
  }, [props.currFilterQueryFormat]);

  return (
    <FilterItemStyle
      a="stretch"
      style={{ cursor: props.onFilterChange ? 'pointer' : 'auto' }}
    >
      <FilterKeyStyle>
        <Typo
          type="label3"
          color={color.gray700}
          style={{ whiteSpace: 'nowrap' }}
        >
          {props.filterOption.columnName}
        </Typo>
      </FilterKeyStyle>
      <FilterKeyStyle className="filter-item-operator">
        <FilterItemOperatorSelector
          dropdownKey={`filter-item-operator-selector-${props.filterOption.columnKey}-${props.tableName}`}
          operator={props.currFilterQueryFormat.operator}
          multiFilter={filterStatusList.length > 1}
          isMultiValueField={props.filterOption.isMultiValueField}
          onChange={
            props.onFilterChange
              ? (v) =>
                  props.onFilterChange?.({
                    ...props.currFilterQueryFormat,
                    operator: v,
                  })
              : undefined
          }
        />
      </FilterKeyStyle>
      <Flex>
        <FilterKeyStyle
          ref={filterRef}
          onClick={() => {
            props.onFilterChange &&
              !props.filterOption.deleteOnly &&
              setFilterDropdownState(props.filterOption);
          }}
        >
          <Tooltip
            disabled={filterStatusList.length < 4}
            tooltipNode={filterStatusList
              .map((filterValue) => filterValue?.filterName)
              .join(', ')}
          >
            <Typo
              type="label3"
              style={{ maxWidth: 260, paddingRight: 6 }}
              ellipsis
            >
              {filterStatusList
                .map((filterValue) => filterValue?.filterName)
                .join(', ')}
            </Typo>
          </Tooltip>
        </FilterKeyStyle>
        {props.onFilterChange && (
          <>
            <FilterKeyStyle
              onClick={(e) => {
                e.stopPropagation();
                props.onFilterChange?.({
                  ...props.currFilterQueryFormat,
                  filterValues: [],
                });
              }}
            >
              <CloseCircleOutlined style={{ fontSize: 14 }} />
            </FilterKeyStyle>
          </>
        )}
      </Flex>

      <FilterItemSelect
        status={dropdownOpenStatus}
        filterOption={props.filterOption}
        currFilterQueryFormat={props.currFilterQueryFormat}
        onChange={props.onFilterChange}
        close={() => setFilterDropdownState(null)}
        triggerEl={filterRef.current}
      />
    </FilterItemStyle>
  );
};

export const FilterItemStyle = styled(Flex)`
  position: relative;
  cursor: pointer;
  flex-wrap: wrap;
  padding: 0px 16px;
  border: 1px solid ${(props) => props.theme.colors.gray400};
  background-color: ${(props) => props.theme.colors.gray100};
  border-radius: 16px;
  gap: 4px;
  .filter-item-operator {
    padding: 0px 6px;
    transition: background-color 0.2s ease-in-out;
    cursor: pointer;
    &:hover {
      background-color: ${(props) => props.theme.colors.gray200};
    }
  }
`;

export const FilterKeyStyle = styled(CenterFlex)`
  height: 30px;
`;

export default FilterItem;
